import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import DateInput from '../inputs/DateInput.js';
import BullDropdown from '../../reproduction/bulls/BullDropdown.js';
import WhatsAppTip from '../WhatsAppTip.js';
import SectionDescription from '../SectionDescription.js';
import { useHints } from '../../layout/HintsContext.js';
import { PLAN_TYPE_FOR_LOT, plansOfType } from './planTypes.js';
import '../../weight/lots/WeightLots.css';
import './LotProtocol.css';

const LOT_PREFIXES = { herd: 'HERD', weight: 'WEIGHT', reproduction: 'REPRO' };
const formatLotCode = (lotType, lotId) =>
  `${LOT_PREFIXES[lotType] || lotType.toUpperCase()}-${String(lotId).padStart(4, '0')}`;

const today = () => new Date().toISOString().split('T')[0];

const REPRO_OUTCOMES = ['pregnant', 'returned_to_estrus', 'aborted', 'calved_alive', 'calved_stillborn'];

// One past cycle's roster + outcomes — reproduction only, since that's the
// only lot type with per-animal outcome history (lots.reproduction_results).
// rows are already scoped to this one assignment_id by the caller.
const RunRoster = ({ rows, bulls, t }) => {
  const [search,        setSearch]        = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [bullFilter,    setBullFilter]    = useState('');

  const bullName = (id) => bulls.find(b => b._id === id)?.name || '—';
  const bullsInRun = Array.from(new Set(rows.map(r => r.bull_id).filter(Boolean)));

  const counts = {};
  rows.forEach(r => { counts[r.outcome] = (counts[r.outcome] || 0) + 1; });

  const filtered = rows.filter(r =>
    (!search || r.tag.toLowerCase().includes(search.toLowerCase())) &&
    (!outcomeFilter || r.outcome === outcomeFilter) &&
    (!bullFilter || r.bull_id === bullFilter)
  );

  return (
    <div className="lp-roster">
      <div className="lp-roster-summary">
        <span className="lp-roster-total">{rows.length} {t('tagged_animals').toLowerCase()}</span>
        {REPRO_OUTCOMES.filter(o => counts[o]).map(o => (
          <span key={o} className={`lp-roster-chip lp-roster-chip-${o}`}>{counts[o]} {t(o)}</span>
        ))}
      </div>

      <div className="lp-roster-filters">
        <input
          type="text"
          placeholder={t('find_tag')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)}>
          <option value="">{t('all_statuses')}</option>
          {REPRO_OUTCOMES.map(o => <option key={o} value={o}>{t(o)}</option>)}
        </select>
        {bullsInRun.length > 0 && (
          <select value={bullFilter} onChange={e => setBullFilter(e.target.value)}>
            <option value="">{t('all')}</option>
            {bullsInRun.map(id => <option key={id} value={id}>{bullName(id)}</option>)}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="wl-empty-note">{t('no_animals')}</div>
      ) : (
        <table className="lp-roster-table">
          <thead>
            <tr>
              <th>{t('tag')}</th>
              <th>{t('reproduction_result')}</th>
              <th>{t('bull')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.tag}>
                <td>{r.tag}</td>
                <td><span className={`lp-outcome-badge lp-outcome-${r.outcome}`}>{t(r.outcome)}</span></td>
                <td>{bullName(r.bull_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const LotProtocol = ({ lotType }) => {
  const { lotId }       = useParams();
  const { t }           = useTranslation();
  const { hintsVisible } = useHints();
  const [hintDismissed, setHintDismissed] = useState(false);
  // Reproduction lots run "protocols"; herd and weight lots run "plans".
  const pt = (protocolKey, planKey) => t(lotType === 'reproduction' ? protocolKey : planKey);

  const [lot,          setLot]          = useState(null);
  const [protocol,     setProtocol]     = useState(null);
  const [allProtocols, setAllProtocols] = useState([]);
  const [assignment,   setAssignment]   = useState(null); // null=loading, false=none
  const [history,      setHistory]      = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [showAssign,   setShowAssign]   = useState(false);
  const [bulls,        setBulls]        = useState([]);
  const [results,      setResults]      = useState([]);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [corralActions, setCorralActions] = useState([]); // resolves timeline_days[].action_ids to labels

  // Form state for assign / change
  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  const [startDate,          setStartDate]          = useState(today());
  const [selectedBullId,     setSelectedBullId]     = useState(''); // reproduction only

  // Submitting the assign form is destructive (it silently ends whatever
  // cycle is currently active — see lots.protocol_assignments.Assign server
  // side), so the button never submits directly: it opens this recap first.
  const [confirmingAssign, setConfirmingAssign] = useState(false);

  const loadAssignment = useCallback(() => {
    Fetch(`api/lots/${lotId}/assignment`, 'GET', null,
      (data) => setAssignment(data || false),
      ()     => setAssignment(false)
    );
    Fetch(`api/lots/${lotId}/assignments`, 'GET', null,
      (data) => setHistory((data || []).filter(a => a.ended_at).reverse()),
      ()     => setHistory([])
    );
  }, [lotId]);

  const loadProtocol = useCallback((protocolId) => {
    if (!protocolId) { setProtocol(null); return; }
    Fetch(`api/data/lots.plans?search_fields=_id&search_values=${protocolId}`, 'get', null,
      (data) => setProtocol((data || [])[0] || null)
    );
  }, []);

  useEffect(() => {
    Fetch(`api/data/lots?search_fields=type,lot_id&search_values=${lotType},${lotId}`, 'get', null,
      (data) => {
        const found = (data || [])[0] || null;
        setLot(found);
        loadProtocol(found?.protocol_id);
      }
    );
    Fetch('api/data/lots.plans', 'get', null, (data) => setAllProtocols(data || []));
    Fetch('api/corral/actions', 'get', null, (data) => setCorralActions(data || []));
    loadAssignment();

    // Per-cycle roster/outcomes are reproduction-only — no equivalent
    // per-animal outcome collection exists for herd/weight lots.
    if (lotType === 'reproduction') {
      Fetch('api/data/reproduction.bulls', 'get', null, (data) => setBulls(data || []));
      Fetch('api/reproduction/results', 'GET', null, (data) => setResults(data || []));
    }
  }, [lotId, lotType, loadAssignment, loadProtocol]);

  const handleAssign = () => {
    if (!selectedProtocolId) return;
    setSaving(true);

    // The assignment endpoint itself keeps lots.protocol_id in sync (see
    // protocol_assignments.Assign) — no separate PATCH to lots needed, and
    // no protocol_start_date to send either: that cache was removed (it
    // could silently drift from the assignment's own start_date, which is
    // the only authoritative one).
    const body = { protocol_id: selectedProtocolId, start_date: startDate };
    if (lotType === 'reproduction' && selectedBullId) {
      body.bull_id = selectedBullId;
    }

    Fetch(`api/lots/${lotId}/assignment`, 'POST',
      JSON.stringify(body),
      () => {
        setSaving(false);
        setShowAssign(false);
        setConfirmingAssign(false);
        loadProtocol(selectedProtocolId);
        setLot(prev => ({ ...prev, protocol_id: selectedProtocolId }));
        loadAssignment();
      },
      () => { setSaving(false); setConfirmingAssign(false); }
    );
  };

  const handleMarkComplete = (stepIndex) => {
    setSaving(true);
    Fetch(`api/lots/${lotId}/assignment/step`, 'POST',
      JSON.stringify({ step_index: stepIndex }),
      () => { setSaving(false); loadAssignment(); },
      () => setSaving(false)
    );
  };

  const handleStartTracking = () => {
    if (!lot?.protocol_id) return;
    setSaving(true);
    // No protocol_start_date to replay (removed) — "start tracking" means
    // starting now, same reasoning as the WhatsApp "start" command.
    const body = { protocol_id: lot.protocol_id, start_date: today() };
    if (lotType === 'reproduction' && lot.reproduction?.bull_id) {
      body.bull_id = lot.reproduction.bull_id;
    }
    Fetch(`api/lots/${lotId}/assignment`, 'POST',
      JSON.stringify(body),
      () => { setSaving(false); loadAssignment(); },
      () => setSaving(false)
    );
  };

  if (!lot) return <div className="wl-empty-note" style={{ padding: 24 }}>…</div>;

  const lotCode = formatLotCode(lotType, lotId);
  const steps = protocol?.timeline_days || [];

  const protocolHints = (() => {
    if (!hintsVisible || hintDismissed) return null;
    if (assignment === null) return null; // still loading
    const hasProtocol = !!lot.protocol_id;
    if (!hasProtocol) return null;

    const tips = assignment
      ? [
          { label: t('wat_protocol_done'), message: `${lotCode} done` },
          { label: t('wat_protocol_stop'), message: `${lotCode} stop` },
        ]
      : [
          { label: t('wat_protocol_start'), message: `${lotCode} start` },
        ];

    return (
      <WhatsAppTip
        title={pt('protocol_progress', 'plan_progress')}
        tips={tips}
        onDismiss={() => setHintDismissed(true)}
      />
    );
  })();

  // Build completion map from active assignment.
  const completionMap = {};
  if (assignment && assignment.progress) {
    assignment.progress.forEach(p => { if (p.completed) completionMap[p.step_index] = p; });
  }

  // Steps in storage/array order aren't necessarily in day order — the
  // system-action lifecycle steps (mark_all_pregnant, mark_lot_archived,
  // notify_birthing_window, mark_lost_pregnancies) were appended by separate
  // migrations over time, each just pushing onto the end of timeline_days
  // regardless of day value (see internal/migrate 046/049/050 in
  // posso-help-api). The backend job that fires them (processDueSystemActions)
  // checks every step independently by its own day, so storage order doesn't
  // affect it — but "current step" here was computed by walking array index
  // order, so it could get stuck pointing at an earlier-indexed, later-day
  // step while later-indexed, earlier-day steps had already silently
  // completed out of view. Fixed by deriving both display order and
  // "current" from start_day, while every read/write of completion still
  // uses each step's original array index (index is the durable identity
  // completion is keyed on, both here and server-side).
  const orderedSteps = steps
    .map((step, index) => ({ step, index }))
    .sort((a, b) => a.step.start_day - b.step.start_day);

  const currentStepIndex = (() => {
    if (!assignment) return orderedSteps[0]?.index ?? 0;
    const next = orderedSteps.find(({ index }) => !completionMap[index]);
    return next ? next.index : orderedSteps[orderedSteps.length - 1]?.index ?? 0;
  })();

  const AssignForm = () => {
    const selectedProtocol = allProtocols.find(p => p._id === selectedProtocolId);
    const selectedBull = bulls.find(b => b._id === selectedBullId);

    return (
      <>
        <div className="lp-assign-form">
          <select
            value={selectedProtocolId}
            onChange={e => { setSelectedProtocolId(e.target.value); setConfirmingAssign(false); }}
          >
            <option value="">{pt('select_protocol', 'select_plan')}</option>
            {plansOfType(allProtocols, PLAN_TYPE_FOR_LOT[lotType]).map(p => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
          <DateInput
            value={startDate}
            onChange={v => { setStartDate(v); setConfirmingAssign(false); }}
            defaultToToday={true}
            required={false}
          />
          {lotType === 'reproduction' && (
            <BullDropdown
              value={selectedBullId}
              onChange={e => { setSelectedBullId(e.target.value); setConfirmingAssign(false); }}
            />
          )}
          <button
            className="lp-assign-btn"
            onClick={() => setConfirmingAssign(true)}
            disabled={saving || !selectedProtocolId}
          >
            {saving ? '…' : pt('assign_protocol', 'assign_plan')}
          </button>
          {showAssign && (
            <button className="lp-cancel-btn" onClick={() => { setShowAssign(false); setConfirmingAssign(false); }}>
              {t('cancel')}
            </button>
          )}
        </div>

        {confirmingAssign && (
          <div className="lp-confirm-panel">
            <div className="lp-confirm-title">{t('confirm_new_cycle_title')}</div>
            <ul className="lp-confirm-summary">
              <li><strong>{pt('protocol_name', 'plan_name')}:</strong> {selectedProtocol?.name || '—'}</li>
              <li><strong>{t('start_date')}:</strong> {startDate}</li>
              {lotType === 'reproduction' && (
                <li><strong>{t('bull')}:</strong> {selectedBull?.name || '—'}</li>
              )}
            </ul>
            {assignment && assignment.start_date && (
              <div className="lp-confirm-warning">
                {t('confirm_new_cycle_active_warning', { date: assignment.start_date })}
              </div>
            )}
            <div className="lp-confirm-actions">
              <button className="lp-assign-btn" onClick={handleAssign} disabled={saving}>
                {saving ? '…' : t('confirm_new_cycle_yes')}
              </button>
              <button className="lp-cancel-btn" onClick={() => setConfirmingAssign(false)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const hasProtocol = !!lot.protocol_id && !!protocol;

  return (
    <div className="wld-page">
      {protocolHints}
      <div className="wld-subpage-title">{pt('protocol_progress', 'plan_progress')}</div>
      <SectionDescription
        title={pt('lot_protocol_section_title', 'lot_plan_section_title')}
        text={t('lot_protocol_section_text')}
      />

      {!hasProtocol && (
        <>
          <div className="wl-empty-note">{pt('no_active_protocol', 'no_active_plan')}</div>
          <AssignForm />
        </>
      )}

      {hasProtocol && (
        <div className="lp-header">
          <div className="lp-protocol-name">{protocol.name}</div>
          {assignment && assignment.start_date && (
            <div className="lp-meta">{t('start_date')}: {assignment.start_date}</div>
          )}
          {!showAssign && (
            <button
              className="lp-change-btn"
              onClick={() => {
                setShowAssign(true);
                setSelectedProtocolId(lot.protocol_id);
                setStartDate(today());
                setSelectedBullId((assignment && assignment.bull_id) || lot.reproduction?.bull_id || '');
              }}
            >
              {pt('change_protocol', 'change_plan')}
            </button>
          )}
        </div>
      )}

      {hasProtocol && showAssign && <AssignForm />}

      {hasProtocol && !assignment && !showAssign && (
        <div className="lp-start-tracking">
          <button className="lp-start-btn" onClick={handleStartTracking} disabled={saving}>
            {saving ? '…' : t('start_tracking')}
          </button>
        </div>
      )}

      {history.length > 0 && (
        <div className="lp-history">
          <div className="lp-history-title">{t('protocol_history')}</div>
          {history.map((run, ri) => {
            const completedCount = (run.progress || []).filter(p => p.completed).length;
            const runProtocol = allProtocols.find(p => p._id === run.protocol_id);
            const isExpanded = lotType === 'reproduction' && expandedRunId === run._id;
            const runRows = isExpanded ? results.filter(r => r.assignment_id === run._id) : [];
            return (
              <div key={ri} className="lp-history-run">
                <div
                  className={`lp-history-run-header ${lotType === 'reproduction' ? 'lp-history-run-clickable' : ''}`}
                  onClick={lotType === 'reproduction' ? () => setExpandedRunId(isExpanded ? null : run._id) : undefined}
                >
                  <span className="lp-history-run-name">
                    {lotType === 'reproduction' && <span className="lp-history-run-chevron">{isExpanded ? '▾' : '▸'}</span>}
                    {runProtocol?.name || run.protocol_id}
                  </span>
                  <span className="lp-history-run-meta">
                    {run.start_date} → {run.ended_at}
                  </span>
                </div>
                <div className="lp-history-run-summary">
                  ✅ {completedCount} {t('steps_completed')}
                  {run.ended_by && ` · ${t('ended_on')} ${t('completed_by_label')} ${run.ended_by}`}
                </div>
                {isExpanded && <RunRoster rows={runRows} bulls={bulls} t={t} />}
              </div>
            );
          })}
        </div>
      )}

      {hasProtocol && steps.length > 0 && (
        <div className="lp-steps">
          {orderedSteps.map(({ step, index }) => {
            const completed  = !!completionMap[index];
            const isCurrent  = !completed && index === currentStepIndex && !!assignment;
            const prog       = completionMap[index];

            return (
              <div key={index} className={`lp-step ${completed ? 'lp-step-done' : isCurrent ? 'lp-step-current' : 'lp-step-upcoming'}`}>
                <div className="lp-step-header">
                  <span className="lp-step-day">
                    {t('day')} {step.start_day}{step.end_day !== step.start_day ? `–${step.end_day}` : ''}
                  </span>
                  <span className="lp-step-badge">
                    {completed  && '✅'}
                    {isCurrent  && <span className="lp-badge-current">{t('step_current')}</span>}
                    {!completed && !isCurrent && <span className="lp-badge-upcoming">{t('step_upcoming')}</span>}
                  </span>
                </div>

                {step.actions && step.actions.length > 0 && (
                  <ul className="lp-actions">
                    {step.actions.map((action, ai) => (
                      <li key={ai}>{action}</li>
                    ))}
                  </ul>
                )}

                {step.action_ids && step.action_ids.length > 0 && (
                  <ul className="lp-actions">
                    {step.action_ids.map((id, ai) => (
                      <li key={ai}>{corralActions.find(a => a._id === id)?.label || id}</li>
                    ))}
                  </ul>
                )}

                {step.note && <div className="lp-step-note">{step.note}</div>}

                {step.system_action && (
                  <div className="lp-sa-tooltip-wrapper">
                    <div className="lp-system-action-badge">
                      <span>⚙️</span>
                      <span>{t(`system_action_${step.system_action}`)}</span>
                    </div>
                    <div className="lp-sa-tooltip">
                      <span className="lp-sa-tooltip-title">{t(`system_action_${step.system_action}_tooltip_title`)}</span>
                      <span className="lp-sa-tooltip-body">{t(`system_action_${step.system_action}_tooltip`)}</span>
                    </div>
                  </div>
                )}

                {completed && prog && (
                  <div className="lp-completed-meta">
                    {t('completed_on')} {prog.completed_at}
                    {prog.completed_by && ` ${t('completed_by_label')} ${prog.completed_by}`}
                  </div>
                )}

                {isCurrent && (
                  <button className="lp-complete-btn" onClick={() => handleMarkComplete(index)} disabled={saving}>
                    {saving ? '…' : t('mark_step_complete')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LotProtocol;
